import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Section, Eyebrow, Headline, Lead } from '../../components/iss/IssPrimitives';
import { RUNNING_CONFIG, RUNNING_TEMPLATE_LINKS } from './runningLinks';
import { EVALUATION_TEMPLATE_LINKS } from '../evaluation/evaluationLinks';
import { documentLinkProps, isPlaceholderLink } from '../templateFile';
import {
  DURATION_OPTIONS,
  formatDisplayDate,
  getDurationError,
  resolveEndDate,
  todayIsoDate,
  type PlannedDuration,
} from './runningDuration';
import '../initiation/PocTypePage.scss';
import apiService from '../../service/ApiService';

type LocationState = {
  pocId?: string;
  running?: {
    plannedDuration?: PlannedDuration;
    plannedStartDate: string;
    plannedEndDate: string;
  };
  [key: string]: unknown;
};

const RunningPhasePage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as LocationState | null) ?? {};
  const pocId = state.pocId;
  const [plannedDuration, setPlannedDuration] = useState<PlannedDuration | ''>(
    state.running?.plannedDuration ?? ''
  );
  const [customEndDate, setCustomEndDate] = useState(
    state.running?.plannedDuration === 'CUSTOM'
      ? state.running.plannedEndDate
      : ''
  );
  const [savedEndDate, setSavedEndDate] = useState<string | null>(
    state.running?.plannedEndDate ?? null
  );
  const [saved, setSaved] = useState(false);
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const durationError = useMemo(
    () => getDurationError(plannedDuration, customEndDate),
    [plannedDuration, customEndDate]
  );
  const canSave = durationError === null;
  const previewEnd =
    plannedDuration && canSave
      ? resolveEndDate(plannedDuration, customEndDate)
      : null;

  const handleSaveDuration = async () => {
    setAttemptedSave(true);
    setSaveError(null);
    if (!plannedDuration || !canSave) return;

    if (!pocId) {
      setSaveError('Missing PoC id. Complete Preparation first.');
      return;
    }

    setIsSaving(true);
    try {
      const response = await apiService.saveTimeline(pocId, {
        plannedDuration,
        plannedEndDate:
          plannedDuration === 'CUSTOM' ? customEndDate : null,
      });
      setSavedEndDate(response.plannedEndDate);
      setSaved(true);
    } catch (err) {
      console.error(err);
      setSaveError(
        'Could not save timeline. Check Network tab (PoC must be in Running).'
      );
      setSaved(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCompleteRunning = async () => {
    if (!saved || !plannedDuration || !savedEndDate) {
      setAttemptedSave(true);
      setSaveError('Save the duration before completing Running.');
      return;
    }

    if (!pocId) {
      setSaveError('Missing PoC id. Complete Preparation first.');
      return;
    }

    setIsCompleting(true);
    setSaveError(null);
    try {
      await apiService.completeRunning(pocId);
      navigate('/pocs/new/evaluation/workspace', {
        state: {
          ...state,
          running: {
            plannedDuration,
            plannedStartDate: todayIsoDate(),
            plannedEndDate: savedEndDate,
          },
        },
      });
    } catch (err) {
      console.error(err);
      setSaveError(
        'Could not complete Running. Save the duration first, then try again (PoC must be in Running). Check Network tab.'
      );
    } finally {
      setIsCompleting(false);
    }
  };

  return (
    <div className="initiation">
      <aside className="initiation__phases" aria-label="PoC phases">
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">1</span>
          <span>Initiation</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">2</span>
          <span>Preparation</span>
        </div>
        <div className="initiation__phase initiation__phase--active">
          <span className="initiation__phase-num">3</span>
          <span>Running</span>
        </div>
        <div className="initiation__phase initiation__phase--disabled">
          <span className="initiation__phase-num">4</span>
          <span>Evaluation</span>
        </div>
      </aside>

      <div className="initiation__main">
        <Section theme="light" spacing="tight">
          <Eyebrow>POC Running</Eyebrow>
          <Headline as="h1" size="3xl">
            Running phase
          </Headline>
          <Lead>
            All sections below are available at the same time — there is no
            step order or progress tracking in this phase. Use the templates
            and guidance while your PoC runs, and set the planned duration at
            the bottom.
          </Lead>

          <div className="phase-gate">
            <p className="phase-gate__title">
              1. Confirm milestone and implementation plan
            </p>
            <p>
              Finalise and document your Milestone Plan and Implementation Plan
              before or during the running period.
            </p>
            <ul className="phase-gate__list">
              <li>
                <a
                  className="scorecard__link"
                  {...documentLinkProps(RUNNING_TEMPLATE_LINKS.implementationPlan.href)}
                >
                  {RUNNING_TEMPLATE_LINKS.implementationPlan.label}
                </a>
                {isPlaceholderLink(
                  RUNNING_TEMPLATE_LINKS.implementationPlan.href
                )
                  ? ' (placeholder URL)'
                  : ''}
              </li>
              <li>
                <a
                  className="scorecard__link"
                  {...documentLinkProps(RUNNING_TEMPLATE_LINKS.milestonePlan.href)}
                >
                  {RUNNING_TEMPLATE_LINKS.milestonePlan.label}
                </a>
                {isPlaceholderLink(RUNNING_TEMPLATE_LINKS.milestonePlan.href)
                  ? ' (placeholder URL)'
                  : ''}
              </li>
            </ul>
          </div>

          <div className="phase-gate">
            <p className="phase-gate__title">2. Launch communication</p>
            <p>
              Announce the PoC launch using the communication template below.
            </p>
            <p>
              <a
                className="scorecard__link"
                {...documentLinkProps(RUNNING_TEMPLATE_LINKS.launchCommunication.href)}
              >
                {RUNNING_TEMPLATE_LINKS.launchCommunication.label}
              </a>
              {isPlaceholderLink(
                RUNNING_TEMPLATE_LINKS.launchCommunication.href
              )
                ? ' (placeholder URL)'
                : ''}
            </p>
          </div>

          <div className="phase-gate">
            <p className="phase-gate__title">3. Set-up hypercare sessions</p>
            <p>
              During the PoC Running phase, teams are encouraged to plan regular
              hypercare sessions to monitor early-stage performance, address
              initial questions, and ensure alignment across stakeholders. These
              sessions may be organized internally within your team or, if
              needed, in coordination with the Innovation Team. If support is
              required, we kindly suggest reaching out to{' '}
              {RUNNING_CONFIG.hypercareSupportContact}.
            </p>
          </div>

          <div className="phase-gate">
            <p className="phase-gate__title">4. Ongoing feedback loops</p>
            <p>
              Collect regular feedback from users and stakeholders throughout
              the running period. Share findings with your PoC owner and local
              stakeholders so issues can be addressed while the trial is still
              active.
            </p>
          </div>

          <div className="phase-gate">
            <p className="phase-gate__title">
              5. Prepare survey &amp; evaluation measures
            </p>
            <p>
              Revisit the success criteria you defined in Preparation. Prepare
              how you will measure each criterion and gather evidence before the
              PoC ends.
            </p>
          </div>

          <div className="phase-gate">
            <p className="phase-gate__title">6. Evaluation report drafting</p>
            <p>
              Use the evaluation report templates below to draft your report
              before Evaluation begins. The signed report is uploaded in the
              Evaluation phase, not here.
            </p>
            <ul className="phase-gate__list">
              <li>
                <a
                  className="scorecard__link"
                  {...documentLinkProps(EVALUATION_TEMPLATE_LINKS.evaluationPptx.href)}
                >
                  {EVALUATION_TEMPLATE_LINKS.evaluationPptx.label}
                </a>
              </li>
              <li>
                <a
                  className="scorecard__link"
                  {...documentLinkProps(EVALUATION_TEMPLATE_LINKS.finalReportDocx.href)}
                >
                  {EVALUATION_TEMPLATE_LINKS.finalReportDocx.label}
                </a>
              </li>
            </ul>
          </div>

          <div className="phase-gate">
            <p className="phase-gate__title">7. PoC duration setup</p>
            <p>
              Choose 1, 3, or 6 months, or a custom end date. Start is treated
              as today for standard lengths (same as the backend). Saving
              calls the timeline API.
            </p>

            <div className="running-duration">
              <div
                className="score-result__buttons"
                role="group"
                aria-label="Planned duration"
              >
                {DURATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={
                      plannedDuration === opt.value
                        ? 'initiation__next'
                        : 'initiation__next initiation__next--secondary'
                    }
                    onClick={() => {
                      setPlannedDuration(opt.value);
                      setSaved(false);
                      setSavedEndDate(null);
                    }}
                    disabled={isSaving}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {plannedDuration === 'CUSTOM' ? (
                <label className="bj-field">
                  <span className="bj-field__label">Planned end date</span>
                  <input
                    className="bj-field__input"
                    type="date"
                    value={customEndDate}
                    min={todayIsoDate()}
                    onChange={(event) => {
                      setCustomEndDate(event.target.value);
                      setSaved(false);
                      setSavedEndDate(null);
                    }}
                    disabled={isSaving}
                  />
                </label>
              ) : null}

              {previewEnd ? (
                <p className="scorecard__notice" role="status">
                  Planned end: {formatDisplayDate(previewEnd)}
                </p>
              ) : null}

              {attemptedSave && durationError ? (
                <p className="bj-field__error">{durationError}</p>
              ) : null}

              {saveError ? (
                <p className="bj-field__error">{saveError}</p>
              ) : null}

              {saved && savedEndDate ? (
                <p className="scorecard__notice" role="status">
                  Duration saved.
                </p>
              ) : null}

              <div className="running-duration__actions">
                <button
                  type="button"
                  className="initiation__next"
                  disabled={isSaving}
                  onClick={handleSaveDuration}
                >
                  {isSaving ? 'Saving...' : 'Save duration'}
                </button>
              </div>
            </div>
          </div>

          <div className="initiation__actions initiation__actions--wrap">
            <button
              type="button"
              className="initiation__link-back"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              onClick={() =>
                navigate('/pocs/new/preparation/review', { state })
              }
            >
              ← Back to Preparation review
            </button>

            <button
              type="button"
              className="initiation__next initiation__next--secondary"
              onClick={() => navigate('/')}
            >
              Back to home
            </button>

            {saved ? (
              <button
                type="button"
                className="initiation__next"
                disabled={isCompleting || isSaving}
                onClick={() => void handleCompleteRunning()}
              >
                {isCompleting
                  ? 'Moving to Evaluation…'
                  : 'Complete Running phase →'}
              </button>
            ) : null}
          </div>
        </Section>
      </div>
    </div>
  );
};

export default RunningPhasePage;
